import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * Centinela caído (models/sentinel.ts): a knight that never lowers his shield. He is modelled in his guard (the
 * shield square before his chest), so every clip is an offset from it and the shield stays up through all of them
 * but the bash, the stun and the fall.
 *
 * Conventions as in human.ts: −X on an arm or leg swings it forward, +X on a shin bends the knee, −X on a forearm
 * bends the elbow; +X on the trunk or the head leans it forward; +Y turns the trunk to its left (its shield
 * shoulder, on −X, comes forward). Flaps: +X swings the front flap's hem forward, the back flap's back.
 *
 * Blows: attack (the shield bash) lands at 0.13 s (it starts drawn back, like every nightmare's attack); block
 * takes the hit at 0.08 s. The march is slow and heavy (1.9 s a stride pair) and every footfall jolts him.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'flapF' | 'flapB'
  | 'armL' | 'foreL' | 'shield' | 'armR' | 'foreR' | 'sword'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** The tabard's flaps hang plumb against the hips' lean and swing late against how the hips moved. */
const flaps = (b: Bones<B>, lean: number, swing: number): void => {
  b.flapF.rot(-lean + swing, 0, 0);
  b.flapB.rot(-lean - swing, 0, 0);
};

const idle = (): Clip<B> =>
  clip<B>('idle', 4, true, (b, t) => {
    const p = phase(t, 4) * TAU;
    const br = Math.sin(p * 2);
    b.root.pos(0, -0.008 * (1 + br), 0);
    b.chest.rot(-0.025 * br, 0.05 * Math.sin(p), 0);
    // He watches the field: a slow turn of the helm, held at each end.
    b.head.rot(0.03 * br, 0.32 * Math.sin(p) * Math.abs(Math.sin(p)) ** 0.4, 0);
    b.armL.rot(0.02 * br);
    b.foreL.rot(-0.02 * br);
    b.armR.rot(0.03 * Math.sin(p + 1), 0, 0.02 * br);
    b.sword.rot(0.04 * Math.sin(p + 1.6));
    flaps(b, 0, 0.04 * Math.sin(p + 0.8));
  }, (t) => {
    const br = Math.sin(phase(t, 4) * TAU * 2);
    return [1 + 0.006 * br, 1 - 0.005 * br, 1 + 0.006 * br];
  });

/** A footfall jolt: an impulse every half cycle, at the quarter. */
const joltAt = (t: number, dur: number, freq: number, damp: number): number => {
  const half = dur / 2;
  return settle((((t - dur / 4) % half) + half) % half, freq, damp);
};

/** The march: heavy, deliberate, the shield held level while the hips roll under it; each step lands hard. */
const walk = (dur = 1.9): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const jolt = Math.max(0, joltAt(t, dur, 2.4, 7));
    const A = 0.36;
    b.legL.rot(-A * sn - 0.03);
    b.legR.rot(A * sn - 0.03);
    b.shinL.rot(0.75 * Math.max(0, cs) ** 1.5 + 0.04);
    b.shinR.rot(0.75 * Math.max(0, -cs) ** 1.5 + 0.04);
    b.root.rot(0, 0.04 * sn, -0.03 * cs).pos(-0.025 * cs, 0.025 * Math.cos(2 * th) - 0.02 - 0.03 * jolt, 0);
    b.body.rot(0.04, 0.08 * sn, 0.02 * cs);
    // The chest turns against the hips so the shield faces dead ahead.
    b.chest.rot(0.03 + 0.02 * jolt, -0.12 * sn, -0.015 * cs);
    b.head.rot(-0.04 - 0.03 * jolt, 0.02 * sn, 0.015 * cs);
    b.armL.rot(0.03 * jolt);
    b.armR.rot(0.18 * Math.sin(th - 0.4), 0, 0.03);
    b.foreR.rot(-0.1 * Math.max(0, Math.sin(th - 0.8)));
    b.sword.rot(-0.12 * Math.sin(th - 0.9));
    flaps(b, 0.04, 0.12 * Math.sin(2 * th - 0.6) + 0.1 * jolt);
  }, (t) => {
    const j = Math.max(0, joltAt(t, dur, 2.4, 9));
    return [1 + 0.02 * j, 1 - 0.03 * j, 1 + 0.02 * j];
  });

/** Drawn back: the shield pulled in, the shield shoulder turned away, weight on the back foot. */
const DRAWN: P = {
  root: [0, 0.12, 0, 0, -0.03, -0.06],
  body: [0.02, -0.15, 0],
  chest: [-0.06, -0.45, 0],
  head: [0.05, 0.45, 0],
  armL: [0.5, 0, 0.2],
  foreL: [-0.45, 0, 0],
  shield: [0, 0.2, 0],
  armR: [-0.25, 0, 0.25],
  foreR: [-0.3, 0, 0],
  legL: [-0.3, 0, -0.05],
  shinL: [0.25, 0, 0],
  legR: [0.3, 0, 0.05],
  shinR: [0.4, 0, 0],
};
/** The bash: a lunge onto the front foot, the shoulder and the whole weight behind the shield. */
const BASH: P = {
  root: [0, -0.15, 0, 0, -0.07, 0.2],
  body: [0.18, 0.2, 0],
  chest: [0.2, 0.55, 0],
  head: [0.12, -0.5, 0],
  armL: [-0.85, 0, 0.1],
  foreL: [0.35, 0, 0],
  shield: [-0.15, -0.15, 0],
  armR: [0.35, 0, 0.3],
  foreR: [-0.2, 0, 0],
  legL: [-0.65, 0, -0.05],
  shinL: [0.55, 0, 0],
  legR: [0.45, 0, 0.05],
  shinR: [0.15, 0, 0],
  flapF: [-0.4, 0, 0],
  flapB: [0.3, 0, 0],
};

const attack = (): Clip<B> =>
  clip<B>('attack', 1.3, false, (b, t) => {
    timeline(b, t, [
      [0, DRAWN],
      [0.05, { ...DRAWN, chest: [-0.08, -0.52, 0], armL: [0.58, 0, 0.22] }, outQuad],
      [0.13, BASH, inCubic],
      [0.4, { ...BASH, root: [0, -0.15, 0, 0, -0.065, 0.19], chest: [0.18, 0.5, 0] }, outCubic],
      [1.3, {}, inOut],
    ], { head: 0.04, flapF: 0.08, flapB: 0.08, sword: 0.06 });
    // The shield shudders on the impact.
    const h = kick(t, 0.13, 6, 9);
    b.shield.rot(0.05 * h, 0, 0.04 * h);
    b.root.pos(0, 0, -0.02 * h);
  }, (t) => thud(t, [[0.13, 0.04]]), 'Golpe de escudo');

/** Something struck his shield from the front: he braces, the shield jolts back into him and he leans into it. */
const block = (): Clip<B> =>
  clip<B>('block', 0.9, false, (b, t) => {
    const brace: P = { root: [0, 0, 0, 0, -0.05, 0], body: [0.1, 0, 0], chest: [0.12, 0, 0], head: [0.15, 0, 0], legL: [-0.25, 0, 0], shinL: [0.35, 0, 0], legR: [0.15, 0, 0], shinR: [0.3, 0, 0], armL: [-0.15, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.06, brace, outQuad],
      [0.4, brace],
      [0.9, {}, inOut],
    ]);
    const h = kick(t, 0.08, 4, 8);
    b.armL.rot(0.18 * h);
    b.foreL.rot(-0.12 * h);
    b.shield.rot(0.08 * h);
    b.chest.rot(-0.06 * h);
    b.root.pos(0, 0, -0.04 * Math.max(0, h));
  }, (t) => thud(t, [[0.08, 0.02]]), 'Bloquea');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.45, false, (b, t) => {
    const q = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    b.chest.rot(-0.12 * q, -0.08 * q, 0.05 * q);
    b.body.rot(-0.05 * q);
    b.head.rot(-0.2 * q, 0.1 * q);
    b.root.pos(0, 0, -0.03 * q);
    b.armR.rot(0.15 * q, 0, 0.1 * q);
    flaps(b, 0, 0.15 * q);
  });

/** Stunned: the shield sags, the knees give a little, the helm lolls. */
const stun = (): Clip<B> =>
  clip<B>('stun', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    b.root.pos(0, -0.05, 0).rot(0.02 * Math.sin(p), 0, 0.04 * Math.cos(p));
    b.body.rot(0.12, 0, 0);
    b.chest.rot(0.15 + 0.04 * Math.sin(p * 2), 0.08 * Math.sin(p), -0.05 * Math.cos(p));
    b.head.rot(0.35 + 0.1 * Math.sin(p + 0.8), 0.25 * Math.cos(p + 0.8), 0.18 * Math.sin(p + 0.8));
    b.armL.rot(0.55, 0, -0.08);
    b.foreL.rot(0.35);
    b.shield.rot(0.2, 0, -0.1);
    b.armR.rot(0.08 * Math.sin(p + 1), 0, 0.04);
    b.sword.rot(0.25);
    b.legL.rot(-0.15);
    b.shinL.rot(0.3);
    b.legR.rot(-0.1);
    b.shinR.rot(0.28 + 0.06 * Math.sin(p));
    flaps(b, 0.12, 0.06 * Math.sin(p));
  });

/**
 * The fall (2.6 s): struck upright, he staggers back a step, the sword drops from his hand, he sinks to his
 * knees (1.1 s, the ground shakes) with the shield sliding down before him, and pitches forward over it (2.1 s).
 */
const die = (): Clip<B> =>
  clip<B>('die', 2.6, false, (b, t) => {
    const kneel: P = { legL: [0.05, 0, -0.08], shinL: [1.55, 0, 0], legR: [0.1, 0, 0.08], shinR: [1.55, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.25, { root: [-0.12, 0, 0, 0, 0.01, -0.08], chest: [-0.3, 0.15, 0], head: [-0.5, -0.2, 0], armL: [0.2, 0, 0.2], foreL: [0.1, 0, 0], armR: [-0.6, 0, 0.6], foreR: [-0.3, 0, 0], legL: [-0.1, 0, 0], legR: [0.2, 0, 0], shinR: [0.2, 0, 0], flapF: [0.3, 0, 0] }, outExpo],
      [0.7, { root: [0.05, 0, 0.04, 0, -0.08, -0.12], body: [0.25, 0, 0], chest: [0.25, 0.1, 0], head: [0.4, 0, 0.2], armL: [0.45, 0, 0.1], foreL: [0.25, 0, 0], shield: [0.15, 0, 0], armR: [0.2, 0, 0.2], sword: [1.4, 0, 0.4, 0, -0.1, 0.05], legL: [-0.3, 0, -0.05], shinL: [0.6, 0, 0], legR: [0.15, 0, 0.05], shinR: [0.7, 0, 0] }, inOut],
      [1.1, { ...kneel, root: [0, 0, 0, 0, -0.5, -0.1], body: [0.2, 0, 0.05], chest: [0.18, 0, 0], head: [0.5, 0, 0.25], armL: [0.6, 0, 0.1], foreL: [0.3, 0, 0], shield: [0.3, 0, 0, 0, -0.25, 0], armR: [0.15, 0, 0.15], sword: [1.6, 0, 0.6, 0, -0.32, 0.1] }, inQuad],
      [1.25, { ...kneel, root: [0, 0, 0, 0, -0.48, -0.1], body: [0.24, 0, 0.05], chest: [0.2, 0, 0], head: [0.6, 0, 0.28], armL: [0.62, 0, 0.1], foreL: [0.3, 0, 0], shield: [0.32, 0, 0, 0, -0.27, 0], armR: [0.15, 0, 0.15], sword: [1.6, 0, 0.6, 0, -0.32, 0.1] }, outBack],
      [1.7, { ...kneel, root: [0, 0, 0, 0, -0.49, -0.1], body: [0.35, 0.1, 0.08], chest: [0.3, 0, 0.05], head: [0.75, 0.2, 0.3], armL: [0.62, 0, 0.1], foreL: [0.3, 0, 0], shield: [0.35, 0, 0, 0, -0.27, 0], armR: [0.25, 0, 0.1], sword: [1.6, 0, 0.6, 0, -0.32, 0.1] }, inOut],
      [2.1, { ...kneel, root: [0, 0, 0, 0, -0.55, 0.05], body: [1.25, 0.1, 0.1], chest: [0.3, 0.05, 0.05], head: [0.2, 0.4, 0.4], armL: [-1.0, 0, 0.4], foreL: [0.2, 0, 0], shield: [0.9, 0, 0.2, 0, -0.1, 0.1], armR: [-1.9, 0, 0.4], foreR: [0, 0, 0], sword: [1.6, 0, 0.6, 0, -0.32, 0.1], flapB: [-0.8, 0, 0] }, inQuad],
      [2.6, { ...kneel, root: [0, 0, 0, 0, -0.55, 0.05], body: [1.2, 0.1, 0.1], chest: [0.28, 0.05, 0.05], head: [0.3, 0.45, 0.45], armL: [-1.05, 0, 0.42], foreL: [0.22, 0, 0], shield: [0.92, 0, 0.22, 0, -0.1, 0.1], armR: [-1.95, 0, 0.42], foreR: [0, 0, 0], sword: [1.6, 0, 0.6, 0, -0.32, 0.1], flapB: [-0.9, 0, 0] }, outBack],
    ], { head: 0.05, flapF: 0.06, flapB: 0.06 });
    b.root.pos(0.01 * kick(t, 1.1, 7, 8) + 0.012 * kick(t, 2.1, 7, 8), 0, 0);
  }, (t) => thud(t, [[1.1, 0.05], [2.1, 0.06]]));

/** The phalanx's horn: a shield raised and struck with the pommel, three times (for the falange forming up). */
const rally = (): Clip<B> =>
  clip<B>('rally', 1.6, false, (b, t) => {
    const up: P = { chest: [-0.08, 0.2, 0], head: [-0.2, 0.15, 0], armL: [-0.35, 0, 0], foreL: [-0.1, 0, 0], armR: [-1.6, 0, -0.5], foreR: [-1.1, 0, 0], sword: [-0.6, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.3, up, inOut],
      [1.25, up],
      [1.6, {}, inOut],
    ]);
    // Three blows of the pommel on the shield's rim.
    for (const t0 of [0.45, 0.75, 1.05]) {
      const s = envelope(t, t0 - 0.12, t0 - 0.02, t0, t0 + 0.12, inCubic);
      b.foreR.rot(0.35 * s);
      b.armR.rot(0.2 * s);
      b.shield.rot(0.04 * kick(t, t0, 7, 10));
    }
  }, (t) => thud(t, [[0.45, 0.012], [0.75, 0.012], [1.05, 0.012]]), 'Formar falange');

/** Clips by library kind (see ../rigs.ts). */
export const sentinelClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), block(), rally()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { sentinel: sentinelClips };
